import React, { useMemo } from "react";
import { Section } from "@/dom/components/Panel/Section";
import { Furniture } from "@/state/Furniture";
import { Row } from "@/dom/components/Panel/Row";
import { Column } from "@/dom/components/Panel/Column";
import { Typography } from "@/dom/components/Typography/Typography";
import { formatPrice } from "@/utils/string";
import css from "./VariantParametersSection.module.css";
import cn from "classnames";
import _ from "lodash";

type Props = {
    furniture: Furniture;
    otherVariants: ReadonlyArray<Furniture>;
    onOtherVariantSelected: (otherVariantId: string) => void;
};

export const VariantParametersSection: React.FC<Props> = ({
    furniture,
    otherVariants,
    onOtherVariantSelected,
}) => {
    const variants = useMemo(() => {
        const variantParameterName = furniture.data.variantParameterName;
        if (!variantParameterName) {
            return [];
        }

        const otherVariantsWithParameter = otherVariants.filter(
            (v) => v.data.variantParameterName === variantParameterName
        );

        return _.orderBy([furniture, ...otherVariantsWithParameter], ["data.name"], ["asc"]);
    }, [furniture, otherVariants]);

    return (
        <Section title={furniture.data.variantParameterName}>
            <Row justify="flex-start">
                {variants.map((variant) => (
                    <Column
                        className={css.main}
                        gap="12px"
                        key={variant.id}
                        onClick={() => onOtherVariantSelected(variant.id)}
                    >
                        <div
                            className={cn(css.imageContainer, {
                                [css.currentVariant]: variant.id === furniture.id,
                            })}
                        >
                            <img src={variant.data.variantParameterImageUrl} />
                        </div>
                        <Column gap="4px">
                            <Typography variant="body" weight="semibold">
                                {variant.data.name}
                            </Typography>
                            <Typography variant="label" color="gray1">
                                {variant.data.description}
                            </Typography>
                        </Column>
                        <Typography variant="body" weight="semibold">
                            {formatPrice(variant.data.price)}
                        </Typography>
                    </Column>
                ))}
            </Row>
        </Section>
    );
};
